/**
 * WeeklyPlanCard + DayRow — surfaces an active weekly plan inside
 * Zone 2 ("This week's focus"). Each day cycles pending → done →
 * skipped → pending on click; "Wrap up the week" opens an inline
 * retrospective form whose answers feed the Coach's memory.
 */
import React, { useState } from 'react';
import { CheckCircle2, Circle, ClipboardCheck, Compass, MinusCircle, X } from 'lucide-react';

export default function WeeklyPlanCard({ plan, togglingDay, onToggleDay, onArchive, onRetrospect }) {
  const [retroOpen, setRetroOpen] = useState(false);
  const [whatStuck, setWhatStuck] = useState('');
  const [whatDidnt, setWhatDidnt] = useState('');
  const [retroSubmitting, setRetroSubmitting] = useState(false);
  const [retroErr, setRetroErr] = useState(null);
  const [retroSummary, setRetroSummary] = useState(null);

  const days = plan.days || [];
  const doneCount = days.filter((d) => d.status === 'done').length;
  const skippedCount = days.filter((d) => d.status === 'skipped').length;
  const pendingCount = days.length - doneCount - skippedCount;
  const remaining = pendingCount;
  const progressPct = days.length === 0 ? 0 : Math.round((doneCount / days.length) * 100);
  // The "Wrap up the week" CTA is most legible when the user has
  // actually engaged with the plan — at least one day is non-pending.
  // If 0/7 are touched the user hasn't started; we still show the CTA
  // but de-emphasized (ghost) so it doesn't pressure them.
  const allDecided = pendingCount === 0;
  const submitRetro = async () => {
    setRetroErr(null);
    if (!whatStuck.trim() && !whatDidnt.trim()) {
      setRetroErr('Tell the Coach at least one thing — what stuck or what didn\'t.');
      return;
    }
    setRetroSubmitting(true);
    try {
      const res = await onRetrospect(plan.id, {
        what_stuck: whatStuck.trim(),
        what_didnt: whatDidnt.trim(),
      });
      setRetroSummary(res?.retrospective || null);
    } catch (e) {
      setRetroErr(e?.response?.data?.detail || 'Could not save your reflection.');
      setRetroSubmitting(false);
    }
  };

  // After a successful retrospect, the parent has cleared the plan
  // from state — we render a brief celebratory summary card before
  // disappearing on the next dashboard reload.
  if (retroSummary) {
    return (
      <section className="pie-card pie-card-elevated" style={{ marginBottom: 24 }} data-testid="pie-weekly-retro-summary">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <ClipboardCheck size={14} style={{ color: 'var(--pie-accent)' }} />
          <span className="pie-eyebrow" style={{ background: 'transparent', padding: 0, color: 'var(--pie-accent)' }}>
            Week wrapped
          </span>
        </div>
        <h2 className="pie-display-md" style={{ marginBottom: 8 }}>
          {Math.round((retroSummary.completion_rate || 0) * 100)}% completion. Coach has the memo.
        </h2>
        <p style={{ fontSize: 14, color: 'var(--pie-text-secondary)', lineHeight: 1.6 }}>
          Your reflection is now part of the Coach&apos;s system context — next conversation will reference what stuck and what didn&apos;t for <em>you</em> specifically.
        </p>
      </section>
    );
  }

  return (
    <section
      className="pie-card pie-card-elevated pie-weekly-plan-card"
      style={{ marginBottom: 24, position: 'relative', overflow: 'hidden' }}
      data-testid="pie-weekly-plan-card"
    >
      <div
        style={{
          position: 'absolute', inset: '0 0 auto 0', height: 4,
          background: 'linear-gradient(90deg, var(--pie-accent) 0%, var(--pie-text) 100%)',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="pie-eyebrow" style={{ background: 'transparent', padding: 0, color: 'var(--pie-accent)' }}>
            <Compass size={12} /> This week&apos;s plan
          </span>
        </div>
        <button
          type="button"
          className="pie-weekly-plan-dismiss"
          onClick={() => onArchive(plan.id)}
          aria-label="Dismiss this plan"
          data-testid="pie-weekly-plan-dismiss"
          title="Dismiss this plan"
        >
          <X size={14} />
        </button>
      </div>
      <h2 className="pie-display-md" style={{ marginBottom: 6, lineHeight: 1.2 }}>
        {plan.plan_title || 'Your week'}
      </h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, fontSize: 13, color: 'var(--pie-text-secondary)' }}>
        <div className="pie-weekly-plan-progress" aria-hidden>
          <div className="pie-weekly-plan-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="pie-mono" style={{ minWidth: 92, textAlign: 'right' }}>
          {doneCount} / {days.length} done
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {days.map((day) => (
          <DayRow
            key={day.day_num}
            day={day}
            disabled={togglingDay !== null}
            busy={togglingDay === day.day_num}
            onToggle={() => onToggleDay(plan.id, day.day_num, day.status)}
          />
        ))}
      </div>
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 11, color: 'var(--pie-text-muted)' }}>
          Tap a row to cycle: pending → done → skipped → pending. {remaining} still open · {skippedCount} skipped.
        </div>
        {!retroOpen && (
          <button
            type="button"
            className={`pie-btn ${allDecided ? 'pie-btn-primary' : 'pie-btn-ghost'} pie-btn-wrap-week`}
            onClick={() => setRetroOpen(true)}
            data-testid="pie-weekly-plan-wrap-up"
          >
            <ClipboardCheck size={13} /> Wrap up the week
          </button>
        )}
      </div>

      {retroOpen && (
        <div className="pie-weekly-retro" data-testid="pie-weekly-retro">
          <div style={{ marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, color: 'var(--pie-text)' }}>
              How did this week land?
            </h3>
            <p style={{ fontSize: 12, color: 'var(--pie-text-muted)', lineHeight: 1.5 }}>
              One line each. The Coach reads this next time so it can build on what worked instead of starting over.
            </p>
          </div>
          <label className="pie-weekly-retro-label" htmlFor="pie-retro-stuck">What stuck?</label>
          <textarea
            id="pie-retro-stuck"
            className="pie-weekly-retro-input"
            placeholder='e.g. "Mornings before email worked. The 1:1 with my manager was the unlock."'
            value={whatStuck}
            onChange={(e) => setWhatStuck(e.target.value)}
            disabled={retroSubmitting}
            rows={2}
            maxLength={600}
            data-testid="pie-weekly-retro-stuck"
          />
          <label className="pie-weekly-retro-label" htmlFor="pie-retro-didnt" style={{ marginTop: 12 }}>What didn&apos;t?</label>
          <textarea
            id="pie-retro-didnt"
            className="pie-weekly-retro-input"
            placeholder='e.g. "Trying to journal at 9pm — too tired. Skip evenings next time."'
            value={whatDidnt}
            onChange={(e) => setWhatDidnt(e.target.value)}
            disabled={retroSubmitting}
            rows={2}
            maxLength={600}
            data-testid="pie-weekly-retro-didnt"
          />
          {retroErr && (
            <div className="pie-weekly-retro-err" data-testid="pie-weekly-retro-err">
              {retroErr}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="pie-btn pie-btn-ghost"
              onClick={() => { setRetroOpen(false); setRetroErr(null); }}
              disabled={retroSubmitting}
              data-testid="pie-weekly-retro-cancel"
            >
              Not yet
            </button>
            <button
              type="button"
              className="pie-btn pie-btn-primary"
              onClick={submitRetro}
              disabled={retroSubmitting}
              data-testid="pie-weekly-retro-submit"
            >
              <ClipboardCheck size={13} />
              {retroSubmitting ? 'Saving…' : 'Submit & teach my Coach'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function DayRow({ day, busy, disabled, onToggle }) {
  const Icon = day.status === 'done' ? CheckCircle2 : day.status === 'skipped' ? MinusCircle : Circle;
  const className =
    day.status === 'done'
      ? 'pie-weekly-day pie-weekly-day-done'
      : day.status === 'skipped'
        ? 'pie-weekly-day pie-weekly-day-skipped'
        : 'pie-weekly-day';
  return (
    <button
      type="button"
      className={className}
      onClick={onToggle}
      disabled={disabled}
      data-testid={`pie-weekly-day-${day.day_num}`}
      data-status={day.status}
    >
      <span className="pie-weekly-day-icon" aria-hidden>
        <Icon size={16} />
      </span>
      <span className="pie-weekly-day-num pie-mono">{day.day_label || `Day ${day.day_num}`}</span>
      <span className="pie-weekly-day-task">
        {day.task}
        {day.target_outcome && (
          <span className="pie-weekly-day-outcome"> — {day.target_outcome}</span>
        )}
      </span>
      {busy && <span className="pie-weekly-day-spinner pie-mono">…</span>}
    </button>
  );
}
